import type { PropsWithChildren, ReactNode } from 'react'
import { useEffect, useSyncExternalStore } from 'react'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import useUser, { isAuthorizedUser, isWorkspaceMember } from 'shared/hooks/use-user'
import {
  featureFlagsClient,
  getFeatureFlagsAvailability,
  subscribeFeatureFlagsAvailability,
} from './client'
import { HeadlessUiAnchorBridge } from './use-headless-ui-anchor'

export const useFeatureFlagsAvailability = (): boolean =>
  useSyncExternalStore(subscribeFeatureFlagsAvailability, getFeatureFlagsAvailability)

export function useSyncFeatureFlagsUserAttributes(): void {
  const { user } = useUser()

  useEffect(() => {
    if (!user) return

    void featureFlagsClient.setAttributes(
      isAuthorizedUser(user)
        ? {
            id: user.id,
            roles: user.roles,
            isDomainOwner: user.isDomainOwner,
            workspaceOwner: user.workspaceOwner,
            isWorkspaceMember: isWorkspaceMember(user),
          }
        : {},
    )
  }, [user])
}

export function FeatureFlagsProvider({ children }: PropsWithChildren): ReactNode {
  useSyncFeatureFlagsUserAttributes()

  return (
    <GrowthBookProvider growthbook={featureFlagsClient}>
      <HeadlessUiAnchorBridge>{children}</HeadlessUiAnchorBridge>
    </GrowthBookProvider>
  )
}
